import { ColumnDef } from "@tanstack/react-table";
import { ChevronDown, ChevronUp } from "baseui/icon";
import classNames from "classnames";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FaCreditCard } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import { AvatarPlaceholder } from "../../../components/avatar/AvatarPlaceholder";
import { SettingsIconButton } from "../../../components/button/SettingsIconButton";
import { SettingsDropdown } from "../../../components/dropdown/SettingsDropdown";
import { LottieLoading } from "../../../components/graphics/LottieLoading";
import { ListPager } from "../../../components/pagination/ListPager";
import { DotsSvg } from "../../../components/svg/DotsSvg";
import { EditPencilSvg } from "../../../components/svg/EditPencilSvg";
import { TrashSvg } from "../../../components/svg/TrashSvg";
import { Table } from "../../../components/table/Table";
import { getInitials } from "../../../constants/constants";
import { updateInvoiceStatusFactory } from "../../../factories/billing/factory-update-invoce-status";
import { TimePeriod } from "../../../hooks/useDetectTimePeriod";
import { useInvoiceFiltersController } from "../../../hooks/useInvoiceFiltersController";
import {
  Invoice,
  SortEnum,
  useDeleteInvoiceMutation,
  useGetInvoicesQuery,
  useGetInvoiceStatsQuery,
  useGetInvoiceStatusesQuery,
  useLazyGetInvoicesQuery,
  useSetInvoiceLabelMutation,
} from "../../../services/BillingService";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import { ExcelExport } from "../../../utils/ExportData";
import { formatBillingDate } from "../billingDates.utils";
import DeletePopup from "../DeletePopup";
import DropStatus from "../DropStatus";
import PayInvoiceModal from "../payments/PayInvoiceModal";
import {
  BILLING_PRIMARY_BUTTON,
  ClientsCell,
  formatMoney,
  invoiceStatusColor,
  PaidIcon,
  StatCard,
} from "../ui/BillingUi";
import {
  billingClientLink,
  invoiceStatusLabel,
} from "../ui/invoiceStatus.utils";
import {
  EXPORT_LIMIT,
  invoiceExportRows,
  outstandingHint,
  withNamedLabels,
} from "./invoiceList.utils";
import { InvoicesListFilters } from "./InvoicesListFilters";

/** Tooltip of the list's footer totals (QA batch 11). */
const FOOTER_HINT =
  "Sum of all invoices that match the filters (all pages, any date, balances as they are). The YTD outstanding card counts only this year's unpaid invoices.";

const PAGE_SIZE = 20;

const formatCurrency = formatMoney;

// Table cell styles from the V49 design.
const HEAD_CELL =
  "px-[10px] py-2.5 text-[12.5px] font-bold text-[#8B92A2] first:pl-[22px] last:pr-[22px]";
const BODY_CELL =
  "bg-transparent px-[10px] py-3.5 text-[13px] text-[#3A4256] first:pl-[22px] last:pr-[22px]";
const FOOT_CELL = "px-[10px] py-3.5 first:pl-[22px] last:pr-[22px]";
const META = {
  headerClassName: HEAD_CELL,
  className: BODY_CELL,
  footerClassName: FOOT_CELL,
};

export const InvoicesListView: React.FC<unknown> = () => {
  const navigate = useNavigate();
  const orgId = getLinkedFirm()?.orgId;

  const [page, setPage] = useState(1);
  const [payInvoice, setPayInvoice] = useState<Invoice | null>(null);
  const [invoiceDelete, setInvoiceDelete] = useState<Invoice | null>(null);
  const [activeInvoiceId, setActiveInvoiceId] = useState("");

  const [sort, setSort] = useState<{ name: SortEnum; order: boolean }>({
    name: SortEnum.InvoiceDate,
    order: false,
  });

  const filters = useInvoiceFiltersController();

  const isAllDateFilter =
    !filters.filter.period || filters.filter.period === TimePeriod.All;

  const currentFiltersKey = useMemo(
    () =>
      JSON.stringify({
        period: filters.filter.period,
        from: filters.from ? filters.from.toISOString() : null,
        to: filters.to ? filters.to.toISOString() : null,
        statusIds: filters.filterByStatus.map((item) => item.value),
        contactIds: filters.filterByContact.map((item) => item.value),
        userIds: filters.filterByUser.map((item) => item.value),
        labelIds: filters.filterByLabel.map((item) => item.value),
      }),
    [
      filters.filter.period,
      filters.from,
      filters.to,
      filters.filterByStatus,
      filters.filterByContact,
      filters.filterByUser,
      filters.filterByLabel,
    ],
  );

  const previousFiltersKeyRef = useRef(currentFiltersKey);

  useEffect(() => {
    if (previousFiltersKeyRef.current !== currentFiltersKey) {
      previousFiltersKeyRef.current = currentFiltersKey;
      setPage(1);
    }
  }, [currentFiltersKey]);

  const { data: stats, isLoading: isLoadingStats } =
    useGetInvoiceStatsQuery(orgId);

  // Filters + sort of the list (also used by Export).
  const listQuery = {
    orgId,
    filterByDate: isAllDateFilter ? [] : ["Custom"],
    fromDate: isAllDateFilter
      ? null
      : filters.from
      ? filters.from.toISOString()
      : null,
    toDate: isAllDateFilter
      ? null
      : filters.to
      ? filters.to.toISOString()
      : null,
    sortBy: sort.name,
    sortOrder: (sort.order ? "asc" : "desc") as "asc" | "desc",
    filterByStatus: filters.filterByStatus.map((item) => item.value),
    filterByClient: filters.filterByContact.map((item) => item.value),
    filterByAssignee: filters.filterByUser.map((item) => item.value),
    filterByLabel: filters.filterByLabel.map((item) => item.value),
  };

  const {
    data: invoices,
    isLoading,
    isFetching,
  } = useGetInvoicesQuery({ ...listQuery, pageNumber: page, pageSize: PAGE_SIZE });

  // BILL-018 (QA batch 17): Export = every invoice the filters match (all
  // pages, up to 5,000), not only the 20 on screen; status words as listed.
  const [loadInvoices] = useLazyGetInvoicesQuery();
  const loadExportRows = useCallback(async () => {
    const rows = await loadInvoices({
      ...listQuery,
      pageNumber: 1,
      pageSize: EXPORT_LIMIT,
    }).unwrap();
    return invoiceExportRows(rows || []);
  }, [listQuery, loadInvoices]);

  const { data: statuses, isLoading: isLoadingStatuses } =
    useGetInvoiceStatusesQuery(orgId || "");

  const [updateInvoice, { isLoading: isLoadingUpdate }] =
    useSetInvoiceLabelMutation();

  const [deleteInvoice] = useDeleteInvoiceMutation();

  const invoicesData = invoices || [];

  const totalRecords = invoices?.[0]?.totalRecords ?? 0;

  const handleSort = useCallback(
    (name: SortEnum) => {
      setPage(1);

      if (name === sort.name) {
        setSort({ name, order: !sort.order });
      } else {
        setSort({ name, order: true });
      }
    },
    [sort.name, sort.order],
  );

  const handleEditInvoice = useCallback(
    (invoice: Invoice) => {
      navigate("./" + invoice.invoiceId + "?isEdit=true");
    },
    [navigate],
  );

  const handleDeleteInvoice = useCallback((invoice: Invoice) => {
    setInvoiceDelete({
      ...invoice,
      id: invoice.invoiceId,
    });
  }, []);

  const handlePayInvoice = useCallback((invoice: Invoice) => {
    setPayInvoice({
      ...invoice,
      id: invoice.invoiceId,
    });
  }, []);

  const updateStatus = useCallback(
    (statusId: string, invoice: Invoice) => {
      const body = updateInvoiceStatusFactory.createModelFromDto({
        invoice,
        status: statusId || null,
      });

      updateInvoice(body);
    },
    [updateInvoice],
  );

  const renderSortIcon = useCallback(
    (name: SortEnum) => {
      if (sort.name !== name) {
        return null;
      }

      return (
        <span className={"ml-0.5 inline-flex text-[#6B4EFF]"}>
          {sort.order ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </span>
      );
    },
    [sort.name, sort.order],
  );

  const renderSortableHeader = useCallback(
    (label: string, sortName: SortEnum) => (
      <div
        className={
          "flex cursor-pointer items-center whitespace-nowrap normal-case"
        }
        onClick={() => handleSort(sortName)}>
        <div>{label}</div>
        {renderSortIcon(sortName)}
      </div>
    ),
    [handleSort, renderSortIcon],
  );

  const columns = useMemo<ColumnDef<Invoice, unknown>[]>(
    () => [
      {
        id: "invoiceNumber",
        header: () => renderSortableHeader("Invoice No.", SortEnum.InvoiceNo),
        enableSorting: false,
        cell: ({ row }) => (
          <span className={"text-[13.5px] text-[#3A4256]"}>
            {row.original.invoiceNumber}
          </span>
        ),
        meta: META,
      },
      {
        id: "customerName",
        header: () => renderSortableHeader("Contact", SortEnum.Customer),
        enableSorting: false,
        cell: ({ row }) => {
          const invoice = row.original;

          return (
            <ClientsCell
              moreCount={invoice?.linkedCustomerCounts}
              clients={[
                {
                  name: invoice?.customerName || "",
                  link: billingClientLink(invoice || {}),
                  isOrganization: invoice?.customerType === "Business",
                },
                ...(invoice?.linkedCustomerNames ?? []).map((customer) => ({
                  name: customer.clientName,
                  isOrganization: customer.customerType === "Business",
                })),
              ]}
            />
          );
        },
        meta: {
          ...META,
          className: `${BODY_CELL} min-w-[200px] max-w-[320px]`,
        },
      },
      {
        id: "invoiceDate",
        header: () =>
          renderSortableHeader("Invoice date", SortEnum.InvoiceDate),
        enableSorting: false,
        cell: ({ row }) =>
          formatBillingDate(row.original.invoiceDate),
        meta: META,
      },
      {
        id: "dueDate",
        header: () => renderSortableHeader("Due date", SortEnum.DueDate),
        enableSorting: false,
        cell: ({ row }) => {
          const invoice = row.original;

          return (
            <span
              className={classNames(
                "font-medium",
                invoice?.status === "OverDue"
                  ? "text-[#E5484D]"
                  : "text-[#3A4256]",
              )}>
              {formatBillingDate(invoice?.dueDate)}
            </span>
          );
        },
        footer: () => (
          // QA batch 11: the footer sums every invoice the filters match (any
          // date, every balance as it is), so it can differ from the "YTD
          // outstanding" card (this year's unpaid invoices only). Say so.
          <div
            className={"text-end text-[13.5px] font-extrabold text-[#1F2433]"}
            title={FOOTER_HINT}>
            Total (all listed):
          </div>
        ),
        meta: META,
      },
      {
        id: "total",
        header: () => renderSortableHeader("Total", SortEnum.Total),
        enableSorting: false,
        cell: ({ row }) => (
          <span className={"tabular-nums"}>
            {formatCurrency(row.original.total)}
          </span>
        ),
        footer: () => (
          <div
            className={
              "text-[13.5px] font-extrabold tabular-nums text-[#1F2433]"
            }>
            {formatCurrency(invoices?.[0]?.sumOfTotal)}
          </div>
        ),
        meta: META,
      },
      {
        id: "balance",
        header: () => renderSortableHeader("Balance", SortEnum.Balance),
        enableSorting: false,
        cell: ({ row }) => (
          <span className={"tabular-nums"}>
            {formatCurrency(row.original.balance)}
          </span>
        ),
        footer: () => (
          <div
            className={
              "text-[13.5px] font-extrabold tabular-nums text-[#1F2433]"
            }
            title={FOOTER_HINT}>
            {formatCurrency(invoices?.[0]?.sumOfBalance)}
          </div>
        ),
        meta: META,
      },
      {
        id: "status",
        // BILL-005 (QA batch 17): sortable like the other columns.
        header: () => renderSortableHeader("Status", SortEnum.Status),
        enableSorting: false,
        cell: ({ row }) => {
          const invoice = row.original;

          return (
            <div
              className={"flex items-center gap-1.5 font-semibold"}
              style={{ color: invoiceStatusColor(invoice?.status) }}>
              {invoice?.status === "Paid" && <PaidIcon />}
              {invoiceStatusLabel(invoice?.status)}
            </div>
          );
        },
        meta: META,
      },
      {
        id: "assignee",
        header: () => renderSortableHeader("Assignee", SortEnum.AssignedUser),
        enableSorting: false,
        cell: ({ row }) => {
          const invoice = row.original;

          if (!invoice?.userId) {
            return null;
          }

          return (
            <div className={"flex items-center text-sm"}>
              <AvatarPlaceholder
                size={"2xs"}
                type={"custom"}
                avatarCustomColor={invoice?.userColor}
                label={getInitials(
                  invoice?.userFirstName || "",
                  invoice?.userLastName || "",
                )}
              />

              <div
                className={
                  "whitespace-nowrap pl-2 text-[13px] normal-case text-[#3A4256]"
                }>
                {`${invoice?.userFirstName} ${invoice?.userLastName}`}
              </div>
            </div>
          );
        },
        meta: META,
      },
      {
        id: "label",
        header: () => renderSortableHeader("Label", SortEnum.Label),
        enableSorting: false,
        cell: ({ row }) => {
          const invoice = row.original;

          return (
            <DropStatus
              invoiceStatus={{
                id: invoice?.labelId,
                name: invoice?.label,
                color: invoice?.labelColor,
              }}
              paymentStatus={null}
              statuses={withNamedLabels(statuses)}
              isLoading={
                (isLoadingUpdate || isFetching) &&
                invoice?.invoiceId === activeInvoiceId
              }
              selectStatus={(status) => {
                setActiveInvoiceId(invoice?.invoiceId);
                updateStatus(status?.id, {
                  ...invoice,
                  id: invoice?.invoiceId,
                });
              }}
            />
          );
        },
        meta: META,
      },
      {
        id: "actions",
        header: () => null,
        enableSorting: false,
        cell: ({ row }) => {
          const invoice = row.original;

          return (
            <div
              className={"flex justify-end"}
              onClick={(event) => event.stopPropagation()}>
              <SettingsDropdown
                actionsList={[
                  {
                    name: "Edit",
                    action: () => handleEditInvoice(invoice),
                    icon: <EditPencilSvg />,
                  },
                  {
                    name: "Pay",
                    action: () => handlePayInvoice(invoice),
                    icon: <FaCreditCard />,
                  },
                  {
                    name: "Delete",
                    action: () => handleDeleteInvoice(invoice),
                    icon: <TrashSvg />,
                    extraClasses: "text-[#DC2626]",
                    iconColor: "text-[#DC2626]",
                  },
                ]}>
                <SettingsIconButton
                  icon={<DotsSvg />}
                  onClick={() => null}
                  size={"small"}
                />
              </SettingsDropdown>
            </div>
          );
        },
        meta: META,
      },
    ],
    [
      activeInvoiceId,
      handleDeleteInvoice,
      handleEditInvoice,
      handlePayInvoice,
      invoices,
      isFetching,
      isLoadingUpdate,
      renderSortableHeader,
      statuses,
      updateStatus,
    ],
  );

  if (isLoading || isLoadingStats || isLoadingStatuses) {
    return <LottieLoading />;
  }

  return (
    <>
      <div
        className={
          "mb-[26px] grid max-w-[960px] grid-cols-3 gap-[22px] max-md:mb-4 max-md:grid-cols-2 max-md:gap-3"
        }>
        {/* Definitions (QA batch 8, server BillingTotals): invoices dated
            1 Jan this year up to today; past due is part of outstanding. */}
        <StatCard
          label={"YTD invoiced"}
          value={formatCurrency(stats?.totalInvoices)}
          hint={"Total of invoices dated this year (up to today)"}
        />
        <StatCard
          label={"YTD outstanding"}
          value={formatCurrency(stats?.totalOutStandings)}
          hint={outstandingHint(stats?.overpaidInvoices)}
        />
        <StatCard
          label={"YTD past due"}
          value={formatCurrency(stats?.totalPastDue)}
          hint={
            "The part of outstanding whose due date was before today (your time zone). Invoices due today are not past due yet."
          }
        />
      </div>

      <div
        className={
          "rounded-[14px] border border-[#E8E8EC] bg-white shadow-[0_1px_2px_rgba(20,22,34,0.04)]"
        }>
        <div
          className={
            "flex flex-wrap items-end justify-between gap-3.5 px-[22px] pb-[18px] pt-5"
          }>
          <InvoicesListFilters controller={filters} />

          <div className={"flex items-center gap-3.5"}>
            <span
              title={"Export"}
              className={
                "flex h-[38px] w-[38px] items-center justify-center rounded-[9px] border border-[#DFE2E8] hover:bg-[#F4F2FF] [&_svg]:h-[20px] [&_svg]:w-[20px]"
              }>
              <ExcelExport
                data={[]}
                loadData={loadExportRows}
                loadError={"Couldn't export the invoices. Try again."}
                fileName={"Invoices"}
              />
            </span>

            <button
              type={"button"}
              onClick={() => navigate("./add")}
              className={BILLING_PRIMARY_BUTTON}>
              New invoice
            </button>
          </div>
        </div>

        {isFetching ? (
          <LottieLoading />
        ) : (
          <>
            {/* Phones: the wide table scrolls inside its own box. */}
            <div className={"max-md:overflow-x-auto"}>
              <Table
                columns={columns}
                data={invoicesData}
                getRowId={(invoice) => invoice.invoiceId}
                onRowClick={(invoice) => navigate("./" + invoice.invoiceId)}
                rowClassName={
                  "cursor-pointer border-b border-[#F4F5F7] hover:bg-[#FAFAFC]"
                }
                theadClassName={
                  "border-y border-[#F0F1F4] text-[12.5px] font-bold text-[#8B92A2]"
                }
                showFooter={!!invoicesData.length}
                footerRowClassName={
                  "sticky bottom-0 left-0 border-t border-[#ECEEF2] bg-[#F6F7F9]"
                }
              />
            </div>

            {!!invoices?.length && (
              <ListPager
                page={page}
                pageSize={PAGE_SIZE}
                total={totalRecords}
                disabled={isFetching}
                onChange={setPage}
              />
            )}
          </>
        )}
      </div>

      {payInvoice && (
        <PayInvoiceModal
          close={() => setPayInvoice(null)}
          invoice={payInvoice}
        />
      )}

      {invoiceDelete && (
        <DeletePopup
          onClose={() => setInvoiceDelete(null)}
          isActive={!!invoiceDelete}
          onDelete={() => {
            deleteInvoice(invoiceDelete);
            setInvoiceDelete(null);
          }}
        />
      )}
    </>
  );
};
